import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import { FaPlus, FaTimes } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import {
  Table,
  Container,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Pagination,
  Button,
} from "react-bootstrap";
import { BsSearch } from "react-icons/bs";

Modal.setAppElement("#root");

const QuizEnroll = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(25);

  const flattenedEnrollments = filteredEnrollments.flatMap((enrollment) =>
    enrollment.selectedSubjects.map((subject) => ({
      ...enrollment,
      subject,
    }))
  );

  const totalPages = Math.ceil(flattenedEnrollments.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentEntries =
    entriesPerPage === "all"
      ? flattenedEnrollments
      : flattenedEnrollments.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (enrollments && enrollments.length > 0) {
      setFilteredEnrollments(enrollments);
    }
  }, [enrollments]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const [studentsRes, coursesRes, enrollmentsRes] = await Promise.all([
        axios.get("/api/student", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/course", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/studenroll", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStudents(studentsRes.data);
      setCourses(coursesRes.data);
      setEnrollments(enrollmentsRes.data);
      setFilteredEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelection = (selectedOptions) => {
    if (!selectedOptions) {
      setSelectedStudents([]);
      return;
    }
    const selectedIds = selectedOptions.map((option) => option.value);
    if (selectedIds.includes("all")) {
      setSelectedStudents(students.map((s) => s._id));
    } else {
      setSelectedStudents(selectedIds);
    }
  };

  const options = [
    { value: "all", label: "Select All" },
    ...students.map((s) => ({
      value: s._id,
      label: `${s.firstname} ${s.lastname}`,
    })),
  ];

  const handleCourseChange = async (selectedOption) => {
    const courseId = selectedOption.value;
    setSelectedCourse(courseId);
    setSelectedSubjects([]);

    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`/api/course/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(response.data.subjects);
    } catch (error) {
      toast.error("Error fetching topics");
    }
  };

  const handleSubjectChange = (subjectId) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((id) => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = enrollments
      .map((enrollment) => {
        const matchedSubjects = enrollment.selectedSubjects.filter((subject) =>
          subject.name.toLowerCase().includes(query)
        );
        const matchesStudent = `${enrollment.studentId?.firstname} ${enrollment.studentId?.lastname}`
          .toLowerCase()
          .includes(query);
        const matchesCourse = enrollment.selectedCourse?.name
          .toLowerCase()
          .includes(query);
        const matchesPayment = enrollment.paymentStatus
          .toLowerCase()
          .includes(query);

        if (matchesStudent || matchesCourse || matchesPayment) return enrollment;
        if (matchedSubjects.length > 0)
          return { ...enrollment, selectedSubjects: matchedSubjects };
        return null;
      })
      .filter(Boolean);

    setFilteredEnrollments(filtered);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStudents.length || !selectedCourse || !selectedSubjects.length) {
      toast.error("All fields are required");
      return;
    }

    const enrollmentRequests = selectedStudents.map((studentId) => ({
      studentId,
      selectedCourse,
      selectedSubjects,
      paymentStatus: "success",
      paymentId: "adminapproved",
      amount: 0,
      orderId: "adminapproved",
    }));

    try {
      await axios.post("/api/studenroll/enroll", {
        enrollments: enrollmentRequests,
      });
      toast.success("Students enrolled successfully!");
      fetchAllData();
      await Promise.all(
        enrollmentRequests.map((enroll) =>
          axios.post("/api/enrollmail/send-enrollemail", {
            studentId: enroll.studentId,
            selectedCourse: enroll.selectedCourse,
            selectedSubjects: enroll.selectedSubjects,
          })
        )
      );
      toast.success("Confirmation emails sent!");
      setModalOpen(false);
    } catch (error) {
      toast.error("Error enrolling students");
    }
  };

  // ✅ CSV Download Function
  const downloadCSV = (downloadAll = false) => {
    const visibleEntries = downloadAll
      ? flattenedEnrollments
      : flattenedEnrollments.slice(indexOfFirst, indexOfLast);

    if (visibleEntries.length === 0) {
      toast.warning("No records available to download!");
      return;
    }

    const csvHeader = ["#", "Student", "Course", "Topic", "Payment Status"];
    const csvRows = visibleEntries.map((entry, index) => [
      downloadAll ? index + 1 : indexOfFirst + index + 1,
      `${entry.studentId?.firstname || ""} ${entry.studentId?.lastname || ""}`,
      entry.selectedCourse?.name || "N/A",
      entry.subject?.name || "N/A",
      entry.paymentStatus || "N/A",
    ]);

    const csvContent = [csvHeader, ...csvRows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute(
      "download",
      downloadAll
        ? "quiz_enrollments_all.csv"
        : `quiz_enrollments_page${currentPage}.csv`
    );
    link.href = url;
    link.click();
  };

  // ✅ Smart Ellipsis Pagination
  const renderPagination = () => {
    if (entriesPerPage === "all" || totalPages <= 1) return null;

    const paginationItems = [];
    paginationItems.push(
      <Pagination.First
        key="first"
        onClick={() => setCurrentPage(1)}
        disabled={currentPage === 1}
      />,
      <Pagination.Prev
        key="prev"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      />
    );

    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      paginationItems.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2)
        paginationItems.push(<Pagination.Ellipsis key="start" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationItems.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1)
        paginationItems.push(<Pagination.Ellipsis key="end" disabled />);
      paginationItems.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    paginationItems.push(
      <Pagination.Next
        key="next"
        onClick={() =>
          setCurrentPage((prev) => Math.min(prev + 1, totalPages))
        }
        disabled={currentPage === totalPages}
      />,
      <Pagination.Last
        key="last"
        onClick={() => setCurrentPage(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <Pagination className="justify-content-center mt-3">
        {paginationItems}
      </Pagination>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="py-4">
      <div className="text-center mb-4">
        <h2 className="fw-bold mb-3">Enroll in a Quiz</h2>
        <button
          className="btn btn-success d-inline-flex align-items-center gap-2"
          onClick={() => setModalOpen(true)}
        >
          <FaPlus /> Enroll Students
        </button>
      </div>

      {/* Search Bar */}
      <InputGroup className="mb-3">
        <InputGroup.Text>
          <BsSearch />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search by name, course, topic, or payment..."
          value={searchQuery}
          onChange={handleSearch}
        />
      </InputGroup>

      {/* Download + Show Entries */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div className="d-flex gap-2">
          <Button variant="success" onClick={() => downloadCSV(false)}>
            Download Visible
          </Button>
          <Button variant="primary" onClick={() => downloadCSV(true)}>
            Download All
          </Button>
        </div>

        <Form.Select
          style={{ width: "180px" }}
          value={entriesPerPage}
          onChange={(e) => {
            const value = e.target.value;
            setEntriesPerPage(value === "all" ? "all" : Number(value));
            setCurrentPage(1);
          }}
        >
          <option value="10">Show 10</option>
          <option value="25">Show 25</option>
          <option value="50">Show 50</option>
          <option value="100">Show 100</option>
          <option value="all">Show All</option>
        </Form.Select>
      </div>

      {/* Table */}
      <div className="table-responsive shadow-sm rounded">
        <Table striped bordered hover className="align-middle">
          <thead className="table-dark text-center">
            <tr>
              <th>#</th>
              <th>Student Name</th>
              <th>Course</th>
              <th>Topic</th>
              <th>Payment Status</th>
            </tr>
          </thead>
          <tbody>
            {currentEntries.length > 0 ? (
              currentEntries.map((entry, index) => (
                <tr key={`${entry._id}-${entry.subject._id}`}>
                  <td className="text-center">
                    {entriesPerPage === "all"
                      ? index + 1
                      : indexOfFirst + index + 1}
                  </td>
                  <td>
                    {entry.studentId?.firstname} {entry.studentId?.lastname}
                  </td>
                  <td>{entry.selectedCourse?.name}</td>
                  <td>{entry.subject?.name}</td>
                  <td className="text-center">
                    <span
                      className={`badge ${
                        entry.paymentStatus === "success"
                          ? "bg-success"
                          : "bg-danger"
                      }`}
                    >
                      {entry.paymentStatus}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  No enrollments found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {renderPagination()}
      <ToastContainer position="bottom-right" autoClose={2500} />
    </Container>
  );
};

export default QuizEnroll;
